import { Box } from '@mui/material';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { SeoLinksGrid } from 'components/molecules/SeoLinksGrid';
import { scanModeInputAtom } from 'containers/deal/recoil/atoms';
import useWidth from 'hooks/useWidth';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { useFilters } from 'services/filters/hooks/useFilters';
import { getFiltersFromUrl } from 'services/filters/utils';
import Rest from 'services/rest';
import { filterZipcodeAtom } from 'services/zipcode/recoil';
import { getStateByZip } from 'shared/services/geo';
import { GetCityByZipcodeParams } from 'shared/types/api/getCityByZipcode';
import { GetModelsByMakeParams } from 'shared/types/api/getModelsByMake';
import { GetNearbyZipcodeParams } from 'shared/types/api/getNearbyZips';
import { MSAPIScanMode } from 'shared/types/marketScan';
import {
  locationsLinks,
  makesModelsLinks,
  SeoLink,
} from 'shared/utils/seoLinksData';

const rest = new Rest();

const Content = styled('div')(() => ({
  overflow: 'hidden',
}));

const Title = styled(Typography)(() => ({
  '&.MuiTypography-root': {
    fontSize: '1.25rem',
  },
}));

export default function SeoLinks(): JSX.Element {
  const width = useWidth();
  const scanMode = useRecoilValue(scanModeInputAtom);
  const isFinance = scanMode === MSAPIScanMode.Retail;
  const [models, setModels] = useState([]);
  const [nearbyLocationLinks, setLinks] = useState([]);
  const router = useRouter();
  const { query } = router;
  const filtersFromUrl = getFiltersFromUrl(query, true);
  const { filters, setFilterValue } = useFilters();
  const setZipcodeAtom = useSetRecoilState(filterZipcodeAtom);
  const leaseOrFinance = isFinance ? 'Finance' : 'Lease';
  const make = filtersFromUrl.make;

  const title = make
    ? `Other ${make} models`
    : 'Find Best Deal - Top Makes & Models';
  const searchedLinks = models.map<SeoLink>((model) => ({
    name: `${make} ${model}`,
    url: `/deals?make=${make}&model=${model}${
      filters.dealType ? `&dealType=${filters.dealType}` : ``
    }`,
  }));

  const zipcodeFromFilter = filters.zipcode;
  const zipcodeFromUrl = filtersFromUrl.zipcode;
  const filteredZip = zipcodeFromUrl ?? zipcodeFromFilter;

  const linkCliked = (url: string) => {
    const zipcode = url.match(/zipcode=\d{5}/);

    if (zipcode[0]) {
      const zipcodeNumbers = zipcode[0].substring(zipcode[0].length - 5);
      setFilterValue('zipcode', zipcodeNumbers);
      setZipcodeAtom(zipcodeNumbers);
    }
  };

  useEffect(() => {
    async function fetchData() {
      if (make) {
        const requestedModels = await rest.getModelsByName({
          makeName: make,
        } as GetModelsByMakeParams);
        setModels(requestedModels.models);
      }
    }
    fetchData();
  }, [make, models.length]);

  useEffect(() => {
    async function fetchData() {
      if (filteredZip) {
        const zips = await rest.getNearbyZips({
          zipcode: filteredZip,
        } as GetNearbyZipcodeParams);

        const citiesByZipcodes = [];
        for (const zip of zips.zipcodes) {
          try {
            const city = await rest.getCityByZipcode({
              zipcode: zip,
            } as GetCityByZipcodeParams);
            const state = getStateByZip(zip);
            citiesByZipcodes.push({
              url: `/deals?zipcode=${zip}`,
              name: `${city.city} ${state.state}`,
            });
            // eslint-disable-next-line no-empty
          } catch (e) {}
        }

        setLinks(citiesByZipcodes);
      }
    }
    fetchData();
  }, [filteredZip]);

  return (
    <>
      {(models.length !== 0 || !make) && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Title
            sx={{
              pb: 1,
              textAlign: width == 'sm' || width == 'xs' ? 'start' : 'center',
            }}
            className={'js-links-makes-&-models'}
            variant='h2'
            gutterBottom
          >
            {title}
          </Title>
          <Content>
            <SeoLinksGrid
              links={make ? searchedLinks : makesModelsLinks}
              classSuffix='makes-&-models'
              formatName={(name) => `${leaseOrFinance} ${name}`}
            ></SeoLinksGrid>
          </Content>
        </Box>
      )}
      <Title
        sx={{
          py: 1,
          textAlign: width == 'sm' || width == 'xs' ? 'start' : 'center',
        }}
        className={'js-links-makes-&-models'}
        variant='h2'
        gutterBottom
      >
        Leasing and Financing Deals Near You
      </Title>
      <Content>
        <SeoLinksGrid
          links={filteredZip ? nearbyLocationLinks : locationsLinks}
          classSuffix='locations'
          formatName={(name) => `${leaseOrFinance} New Car In ${name}`}
          onLinkClick={linkCliked}
        ></SeoLinksGrid>
      </Content>
    </>
  );
}
